import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Modal } from '@ai-tutor/ui';
import { notify } from '@/lib/notify';
import { isStepConflict } from '@/lib/stepConflict';
import { lessonApi } from '@/lib/api-client';
import { MindmapSkeleton } from '../skeletons/PanelSkeletons';
import { PanelFallback } from '@/components/core/common/feedback/PanelFallback';
import { useActivityTracking } from '@/hooks/useActivityTracking';
import { queryKeys } from '@/config/query-client';
import { conceptKey, parseMindmapMarkdown } from '@/lib/mindmap-markdown';
import type { MindmapConcept } from '@/types/api';
import { MindmapCanvas } from './mindmap/MindmapCanvas';
import {
  MindmapConceptPanel,
  type ConceptPanelView,
} from './mindmap/MindmapConceptPanel';

// Concept visuals land seconds to minutes after the map; the backend stops
// marking them pending after 15 minutes, which ends this poll.
const VISUALS_POLL_MS = 5000;

interface MindmapRendererProps {
  onProgressUpdate?: (mediaType: string, progress: number) => void;
  lessonId?: string;
}

/**
 * The Mindmap sub-tab: fetches the lesson's mindmap markdown and draws it
 * with `MindmapCanvas` (a two-sided React Flow layout in the marketing
 * site's style).
 *
 * Replaced markmap. That left-to-right d3 tree could not take the learning
 * tokens (it painted hex into SVG attributes) and needed two teardown
 * workarounds for d3-zoom transitions outliving their `<svg>`. React Flow
 * mounts and unmounts cleanly, so the fullscreen view is simply a second
 * canvas inside the shared `Modal`, drawn from the same parsed outline.
 */
export function MindmapRenderer({
  onProgressUpdate,
  lessonId: propLessonId,
}: MindmapRendererProps) {
  const { id: paramLessonId } = useParams<{ id: string }>();
  const lessonId = propLessonId || paramLessonId;

  // Mount/unmount activity tracking for the Mindmap sub-tab.
  useActivityTracking({ contentType: 'mindmap', lessonId });

  const [isFullscreen, setIsFullscreen] = useState(false);

  // Poll mindmap endpoint
  const {
    data: mindmapData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: queryKeys.lessons.mindmap(lessonId || ''),
    queryFn: () => lessonApi.getLessonMindmap(lessonId as string),
    refetchInterval: query => {
      const data = query.state.data;
      if (data?.status === 'failed') return false;
      // Keep polling if status flipped to completed but the body
      // (markdown_content) hasn't been flushed yet - avoids a "stuck
      // empty" state when the artifact write lags status.
      if (data?.status === 'completed' && data.markdown_content) {
        // The map is done, but concept visuals are made after it: keep
        // polling, slower, until each pending one lands.
        return data.concepts?.some(c => c.visual_pending)
          ? VISUALS_POLL_MS
          : false;
      }
      return 3000;
    },
    staleTime: 0,
    retry: 3,
    enabled: !!lessonId,
  });

  // Markdown -> outline. `parseMindmapMarkdown` strips a wrapping code fence
  // (some LLM runs add one) and returns null on a parse failure.
  const tree = useMemo(
    () =>
      mindmapData?.markdown_content
        ? parseMindmapMarkdown(mindmapData.markdown_content)
        : null,
    [mindmapData?.markdown_content]
  );

  useEffect(() => {
    if (tree) onProgressUpdate?.('mindmap', 100);
  }, [tree, onProgressUpdate]);

  const panel = useConceptPanel(mindmapData?.concepts, isFullscreen);

  if (!lessonId) {
    return <div className='text-destructive'>No lesson ID provided</div>;
  }

  const handleRetry = async () => {
    try {
      await lessonApi.retryStep(lessonId, 'mindmap');
      notify.success('Retrying mindmap generation...');
      refetch();
    } catch (err: unknown) {
      // 409 = nothing to retry (already generated): show the real state.
      if (isStepConflict(err)) {
        notify.info(err.detail);
        refetch();
        return;
      }
      console.error('Failed to retry mindmap generation:', err);
      notify.error(
        (err as Error).message || 'Failed to retry mindmap generation'
      );
    }
  };

  // Initial-load and in-flight states fall through to the same panel
  // skeleton ArtifactGate uses above us - keeps the visual continuous
  // and lets the gate own the status pill.
  if (isLoading || !mindmapData) {
    // ArtifactGate has already gated on `pipelineStatus.mindmap ===
    // 'completed'`, so this branch is a GET round-trip, not a pipeline
    // run. Fetch-mode copy keeps the message honest.
    return <MindmapSkeleton mode='fetching' />;
  }
  if (mindmapData.status === 'pending' || mindmapData.status === 'processing') {
    return <MindmapSkeleton />;
  }
  // Status flipped to completed but markdown_content not flushed yet -
  // keep the skeleton while polling above catches up.
  if (mindmapData.status === 'completed' && !mindmapData.markdown_content) {
    return <MindmapSkeleton />;
  }

  // Failed state - ArtifactGate normally catches above us; this fires
  // when the gate is bypassed or the artifact GET itself errors.
  if (mindmapData.status === 'failed' || error) {
    return (
      <PanelFallback
        variant='step'
        title='Mindmap generation failed'
        description={
          mindmapData?.error?.message ||
          "We couldn't generate the mindmap. Retry, or reload the page if it keeps failing."
        }
        primary={{ label: 'Retry', onClick: handleRetry }}
        secondary={{
          label: 'Reload page',
          onClick: () => window.location.reload(),
        }}
      />
    );
  }

  if (mindmapData.status === 'completed' && tree) {
    return (
      <>
        <MindmapCanvas
          tree={tree}
          mode='inline'
          onToggleFullscreen={() => setIsFullscreen(true)}
          {...panel.canvasProps}
          aside={panel.inlineAside}
        />
        <Modal
          isOpen={isFullscreen}
          onClose={() => setIsFullscreen(false)}
          size='full'
          ariaLabel='Mindmap'
          showCloseButton={false}
          contentClassName='p-0'
        >
          <div className='h-[85vh] w-[92vw] max-w-full'>
            <MindmapCanvas
              tree={tree}
              mode='fullscreen'
              onToggleFullscreen={() => setIsFullscreen(false)}
              {...panel.canvasProps}
              aside={panel.fullscreenAside}
            />
          </div>
        </Modal>
      </>
    );
  }

  // Default fallback
  return (
    <div className='py-8 text-center text-muted-foreground'>
      No mindmap available
    </div>
  );
}

/**
 * The concept panel's state: which concept is open, and the ones before it
 * (a prerequisite opened from the panel can be backed out of). A concept of
 * this lesson also moves the map to its node.
 */
function useConceptPanel(
  concepts: MindmapConcept[] | undefined,
  isFullscreen: boolean
) {
  const [stack, setStack] = useState<ConceptPanelView[]>([]);
  const byKey = useMemo(
    () =>
      new Map((concepts ?? []).map(c => [conceptKey(c.name), c.concept_id])),
    [concepts]
  );
  const conceptIdFor = useCallback(
    (label: string) => byKey.get(conceptKey(label)),
    [byKey]
  );
  const open = useCallback(
    (view: ConceptPanelView, replace: boolean) =>
      setStack(cur => (replace ? [view] : [...cur, view])),
    []
  );
  const onOpenConcept = useCallback(
    (conceptId: string) => open({ kind: 'lesson', conceptId }, true),
    [open]
  );
  const close = useCallback(() => setStack([]), []);
  const current = stack[stack.length - 1] ?? null;

  const aside =
    current && concepts ? (
      <MindmapConceptPanel
        view={current}
        concepts={concepts}
        onClose={close}
        onOpenLesson={conceptId => open({ kind: 'lesson', conceptId }, false)}
        onOpenMindspace={conceptId =>
          open({ kind: 'mindspace', conceptId }, false)
        }
        onBack={
          stack.length > 1 ? () => setStack(s => s.slice(0, -1)) : undefined
        }
      />
    ) : null;

  // One panel at a time: the inline map stays mounted under the fullscreen one.
  return {
    inlineAside: isFullscreen ? null : aside,
    fullscreenAside: isFullscreen ? aside : null,
    canvasProps: byKey.size
      ? {
          conceptIdFor,
          onOpenConcept,
          focusConceptId: current?.kind === 'lesson' ? current.conceptId : null,
        }
      : {},
  };
}
